import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DrawOutlined from '@mui/icons-material/DrawOutlined';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import { Box } from '@mui/material';
import { FormattedMessage } from 'intl';
import { ReportTranslationKeys } from 'intl/keys';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ReportType } from 'shared/domain/report/types/model/common';
import { SectionConfigOnView } from 'shared/domain/report/types/onView/common';
import {
  DEFAULT_ISSUE_ATTACHMENTS_CONFIG_ON_VIEW,
  getDefaultInspectionSectionConfigsOnView,
} from 'shared/domain/report/mapping/reportConfigOnView';
import {
  ReportAdditionalInformationSection,
  ReportContentListItem,
  REPORT_CONTENT_LIST_SX,
} from '../../formUi';
import {
  useReportMessageAttachmentsAvailability,
  useReportSectionAvailability,
} from '../../contexts/ReportSectionAvailabilityContext';
import { useSyncUnavailableReportSections } from '../../hooks/useSyncUnavailableReportSections';
import {
  gatedSectionAvailability,
  ReportSectionId,
  sectionUnavailableProps,
} from '../../model';
import {
  ReportFieldsContentSelect,
  ReportSelectedFieldsMultiselect,
} from 'components/report/ReportFieldsSelector';
import { buildAttachmentsScopeLabel, updateDisplayStyle } from '../model';
import { INSPECTION_REPORT_DISPLAY_STYLES } from '../adapters/sectionDisplayStyleOptions';
import { IssueAttachmentsRowPresentational } from '../presentational';
import {
  InspectionBooleanRow,
  InspectionSectionRow,
} from './presentational';

export function InspectionContentSelection(): ReactElement {
  const intl = useIntl();
  const { values, setValues } = useInputForm();
  const availability = useReportSectionAvailability();
  const messageAttachmentsAvailability =
    useReportMessageAttachmentsAvailability();
  useSyncUnavailableReportSections(
    availability,
    messageAttachmentsAvailability,
    ReportType.inspection
  );

  const sectionConfigDefaults = useMemo(
    () => getDefaultInspectionSectionConfigsOnView(intl),
    [intl]
  );

  const generalStandardAttachments: SectionConfigOnView =
    values.generalStandardAttachments ??
    sectionConfigDefaults.generalStandardAttachments;
  const sectionAttachments: SectionConfigOnView =
    values.sectionAttachments ?? sectionConfigDefaults.sectionAttachments;
  const controlledElementStandardAttachments: SectionConfigOnView =
    values.controlledElementStandardAttachments ??
    sectionConfigDefaults.controlledElementStandardAttachments;
  const controlledElementAttachments: SectionConfigOnView =
    values.controlledElementAttachments ??
    sectionConfigDefaults.controlledElementAttachments;
  const issueAttachments =
    values.issueAttachments ?? DEFAULT_ISSUE_ATTACHMENTS_CONFIG_ON_VIEW(intl);

  const templateSectionEnabled = values.templateSectionEnabled ?? false;
  const checklistSectionEnabled = values.checklistSectionEnabled ?? true;
  const issueDataSectionEnabled = values.issueDataSectionEnabled ?? true;

  const setBoolean = (key: string, enabled: boolean): void => {
    setValues(key, enabled);
  };

  const setSectionConfig = (
    key: ReportSectionId,
    config: SectionConfigOnView
  ): void => {
    setValues(key, config);
  };

  const updateSectionDisplayStyle = (
    key: ReportSectionId,
    config: SectionConfigOnView,
    displayStyle: Parameters<typeof updateDisplayStyle>[1]
  ): void => {
    setValues(key, {
      ...config,
      displayStyle: updateDisplayStyle(intl, displayStyle),
    });
  };

  const issueDataUnavailable = sectionUnavailableProps(availability.issueRegister);

  return (
    <Box sx={REPORT_CONTENT_LIST_SX} data-qa='report_inspection_content_list'>
      <InspectionBooleanRow
        icon={<ArticleOutlined />}
        labelId={ReportTranslationKeys.partInspectionData}
        enabled={values.inspectionInfo ?? true}
        onEnabledChange={(enabled) => setBoolean('inspectionInfo', enabled)}
        sectionAvailability={availability.inspectionInfo}
        data-qa='report_part_inspection_info'
      />

      <InspectionBooleanRow
        icon={<DescriptionOutlined />}
        labelId={ReportTranslationKeys.partSummary}
        enabled={values.summary ?? true}
        onEnabledChange={(enabled) => setBoolean('summary', enabled)}
        sectionAvailability={availability.summary}
        data-qa='report_part_summary'
      />

      <ReportContentListItem
        icon={<GridViewOutlined />}
        title={<FormattedMessage id={ReportTranslationKeys.accordionTemplateData} />}
        switchAriaLabelId={ReportTranslationKeys.accordionTemplateData}
        enabled={templateSectionEnabled}
        onEnabledChange={(enabled) =>
          setBoolean('templateSectionEnabled', enabled)
        }
        contentVariant='nested'
        data-qa='report_template_section'
      >
        <InspectionBooleanRow
          variant='nested'
          labelId={ReportTranslationKeys.templateGeneralStandard}
          enabled={values.generalStandard ?? false}
          onEnabledChange={(enabled) => setBoolean('generalStandard', enabled)}
          sectionAvailability={gatedSectionAvailability(
            templateSectionEnabled,
            availability.generalStandard,
            ReportTranslationKeys.sectionDisabledParentTemplateData
          )}
          data-qa='report_template_general_standard'
        />
        <InspectionSectionRow
          variant='nested'
          labelId={ReportTranslationKeys.templateGeneralStandardAttachments}
          config={generalStandardAttachments}
          onConfigChange={(next) =>
            setSectionConfig(ReportSectionId.generalStandardAttachments, next)
          }
          sectionAvailability={gatedSectionAvailability(
            templateSectionEnabled,
            availability.generalStandardAttachments,
            ReportTranslationKeys.sectionDisabledParentTemplateData
          )}
          onDisplayStyleChange={(displayStyle) =>
            updateSectionDisplayStyle(
              ReportSectionId.generalStandardAttachments,
              generalStandardAttachments,
              displayStyle
            )
          }
          data-qa='report_template_general_standard_attachments'
        />
      </ReportContentListItem>

      <ReportContentListItem
        icon={<AssignmentOutlined />}
        title={<FormattedMessage id={ReportTranslationKeys.partChecklist} />}
        switchAriaLabelId={ReportTranslationKeys.partChecklist}
        enabled={checklistSectionEnabled}
        onEnabledChange={(enabled) =>
          setBoolean('checklistSectionEnabled', enabled)
        }
        contentVariant='nested'
        data-qa='report_checklist_section'
      >
        <InspectionBooleanRow
          variant='nested'
          labelId={ReportTranslationKeys.templateSectionDescriptions}
          enabled={values.sectionDescriptions ?? false}
          onEnabledChange={(enabled) =>
            setBoolean('sectionDescriptions', enabled)
          }
          sectionAvailability={gatedSectionAvailability(
            checklistSectionEnabled,
            availability.sectionDescriptions,
            ReportTranslationKeys.sectionDisabledParentChecklist
          )}
          data-qa='report_template_section_descriptions'
        />
        <InspectionSectionRow
          variant='nested'
          labelId={ReportTranslationKeys.templateSectionAttachments}
          config={sectionAttachments}
          onConfigChange={(next) =>
            setSectionConfig(ReportSectionId.sectionAttachments, next)
          }
          sectionAvailability={gatedSectionAvailability(
            checklistSectionEnabled,
            availability.sectionAttachments,
            ReportTranslationKeys.sectionDisabledParentChecklist
          )}
          onDisplayStyleChange={(displayStyle) =>
            updateSectionDisplayStyle(
              ReportSectionId.sectionAttachments,
              sectionAttachments,
              displayStyle
            )
          }
          data-qa='report_template_section_attachments'
        />
        <InspectionBooleanRow
          variant='nested'
          labelId={ReportTranslationKeys.templateControlledElementStandard}
          enabled={values.controlledElementStandard ?? false}
          onEnabledChange={(enabled) =>
            setBoolean('controlledElementStandard', enabled)
          }
          sectionAvailability={gatedSectionAvailability(
            checklistSectionEnabled,
            availability.controlledElementStandard,
            ReportTranslationKeys.sectionDisabledParentChecklist
          )}
          data-qa='report_template_controlled_element_standard'
        />
        <InspectionSectionRow
          variant='nested'
          labelId={
            ReportTranslationKeys.templateControlledElementStandardAttachments
          }
          config={controlledElementStandardAttachments}
          onConfigChange={(next) =>
            setSectionConfig(
              ReportSectionId.controlledElementStandardAttachments,
              next
            )
          }
          sectionAvailability={gatedSectionAvailability(
            checklistSectionEnabled,
            availability.controlledElementStandardAttachments,
            ReportTranslationKeys.sectionDisabledParentChecklist
          )}
          onDisplayStyleChange={(displayStyle) =>
            updateSectionDisplayStyle(
              ReportSectionId.controlledElementStandardAttachments,
              controlledElementStandardAttachments,
              displayStyle
            )
          }
          data-qa='report_template_controlled_element_standard_attachments'
        />
        <InspectionSectionRow
          variant='nested'
          labelId={ReportTranslationKeys.inspectionControlledElementAttachments}
          config={controlledElementAttachments}
          onConfigChange={(next) =>
            setSectionConfig(ReportSectionId.controlledElementAttachments, next)
          }
          sectionAvailability={gatedSectionAvailability(
            checklistSectionEnabled,
            availability.controlledElementAttachments,
            ReportTranslationKeys.sectionDisabledParentChecklist
          )}
          onDisplayStyleChange={(displayStyle) =>
            updateSectionDisplayStyle(
              ReportSectionId.controlledElementAttachments,
              controlledElementAttachments,
              displayStyle
            )
          }
          data-qa='report_inspection_controlled_element_attachments'
        />
        <InspectionBooleanRow
          variant='nested'
          labelId={ReportTranslationKeys.inspectionShowNotApplicable}
          enabled={values.showNotApplicable ?? false}
          onEnabledChange={(enabled) => setBoolean('showNotApplicable', enabled)}
          sectionAvailability={gatedSectionAvailability(
            checklistSectionEnabled,
            { available: true },
            ReportTranslationKeys.sectionDisabledParentChecklist
          )}
          data-qa='report_inspection_show_not_applicable'
        />
        <InspectionBooleanRow
          variant='nested'
          labelId={ReportTranslationKeys.inspectionShowUnchecked}
          enabled={values.showUnchecked ?? false}
          onEnabledChange={(enabled) => setBoolean('showUnchecked', enabled)}
          sectionAvailability={gatedSectionAvailability(
            checklistSectionEnabled,
            { available: true },
            ReportTranslationKeys.sectionDisabledParentChecklist
          )}
          data-qa='report_inspection_show_unchecked'
        />
      </ReportContentListItem>

      <ReportContentListItem
        icon={<CampaignOutlined />}
        title={<FormattedMessage id={ReportTranslationKeys.accordionIssueData} />}
        switchAriaLabelId={ReportTranslationKeys.accordionIssueData}
        enabled={issueDataSectionEnabled}
        onEnabledChange={(enabled) =>
          setBoolean('issueDataSectionEnabled', enabled)
        }
        unavailable={issueDataUnavailable.unavailable}
        unavailableReasonId={issueDataUnavailable.unavailableReasonId}
        contentVariant='nested'
        data-qa='report_issue_data_section'
      >
        <Box
          sx={{
            pt: 0.5,
            pb: 0.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            '& .MuiFormControl-root': { margin: 0, width: '100%' },
          }}
        >
          <ReportFieldsContentSelect />
          <ReportSelectedFieldsMultiselect />
        </Box>
        <IssueAttachmentsRowPresentational
          variant='nested'
          config={issueAttachments}
          onConfigChange={(next) => setValues('issueAttachments', next)}
          sectionAvailability={gatedSectionAvailability(
            issueDataSectionEnabled,
            availability.issueAttachments,
            ReportTranslationKeys.sectionDisabledParentIssueData
          )}
          onDisplayStyleChange={(displayStyle) =>
            setValues('issueAttachments', {
              ...issueAttachments,
              displayStyle: updateDisplayStyle(intl, displayStyle),
            })
          }
          onScopeChange={(scope) =>
            setValues('issueAttachments', {
              ...issueAttachments,
              scope: {
                _id: scope,
                label: buildAttachmentsScopeLabel(intl, scope),
              },
            })
          }
          displayStyleOptions={INSPECTION_REPORT_DISPLAY_STYLES}
        />
        <InspectionBooleanRow
          variant='nested'
          labelId={ReportTranslationKeys.issueHustroLinks}
          enabled={values.linkToHustro ?? true}
          onEnabledChange={(enabled) => setBoolean('linkToHustro', enabled)}
          sectionAvailability={gatedSectionAvailability(
            issueDataSectionEnabled,
            { available: true },
            ReportTranslationKeys.sectionDisabledParentIssueData
          )}
          data-qa='report_issue_hustro_links'
        />
      </ReportContentListItem>

      <InspectionBooleanRow
        icon={<DrawOutlined />}
        labelId={ReportTranslationKeys.partSignatureSpace}
        enabled={values.signatures ?? true}
        onEnabledChange={(enabled) => setBoolean('signatures', enabled)}
        sectionAvailability={availability.signatures}
        data-qa='report_part_signatures'
      />

      <ReportAdditionalInformationSection />
    </Box>
  );
}
